import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, PenLine, Loader2, CheckCircle } from 'lucide-react';
import ExerciseList from '@/components/ExerciseList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WritingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [text, setText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);

  useEffect(() => {
    fetchExercises();
  }, [user?.level]);

  const fetchExercises = async () => {
    try {
      const params = new URLSearchParams();
      params.append('exercise_type', 'writing');
      if (user?.level) params.append('level', user.level);
      
      const response = await axios.get(`${API}/exercises?${params}`);
      setExercises(response.data);
      if (response.data.length > 0) {
        const firstAvailable = response.data.find(e => !e.is_locked) || response.data[0];
        setCurrentExercise(firstAvailable);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExercise = async (exercise) => {
    if (exercise.is_locked) {
      toast.error('Este ejercicio requiere Premium');
      return;
    }
    setCurrentExercise(exercise);
    setText('');
    setFeedback(null);
    
    // Track exercise view for statistics
    try {
      await axios.post(`${API}/exercises/${exercise.id}/track-view`);
    } catch (error) {
      console.log('Failed to track view');
    }
  };

  const handleSubmit = async () => {
    if (!currentExercise) return;

    const minWords = currentExercise.content?.word_count?.min || currentExercise.content?.min_words || 100;
    const wordCount = text.trim().split(/\s+/).filter(w => w).length;

    if (wordCount < minWords * 0.7) {
      toast.error(`Tu texto es muy corto. Mínimo recomendado: ${minWords} palabras`);
      return;
    }

    setSubmitting(true);
    try {
      // Build prompt from new format or legacy format
      let prompt = currentExercise.content?.prompt || '';
      if (currentExercise.content?.question) {
        prompt = currentExercise.content.question;
      }
      if (currentExercise.content?.context) {
        prompt = currentExercise.content.context + '\n\n' + prompt;
      }
      if (currentExercise.content?.notes && currentExercise.content.notes.length > 0) {
        prompt += '\n\nNotes to include: ' + currentExercise.content.notes.join(', ');
      }

      const response = await axios.post(`${API}/ai/writing-feedback`, {
        exercise_id: currentExercise.id,
        text,
        prompt
      });
      
      setFeedback(response.data);
      toast.success('¡Feedback recibido!');
    } catch (error) {
      console.error('Error getting feedback:', error);
      toast.error('Error al obtener feedback. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setText('');
    setFeedback(null);
  };

  const wordCount = text.trim().split(/\s+/).filter(w => w).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando ejercicios...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <div className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-green-500" />
              <span className="font-medium text-slate-900">Writing Practice</span>
            </div>
            {user?.level && (
              <span className={`level-badge level-${user.level.toLowerCase()}`}>
                {user.level}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Writing Paper Guidance Notes - shown above exercise list for C1 */}
        {exercises.some(e => e.content?.guidance_notes) && (
          <div className="mb-6 p-4 bg-slate-50 border border-slate-300 rounded-sm" data-testid="writing-guidance-notes">
            <p className="font-bold text-slate-900 mb-3">WRITING PAPER GUIDANCE NOTES</p>
            <ul className="space-y-1 text-sm text-slate-800 leading-relaxed">
              <li>• You must answer <span className="font-bold">BOTH</span> Part 1 and <span className="font-bold">ONE</span> question from Part 2.</li>
              <li>• Pay close attention to the word limits: answers that are significantly under or over the word count may not demonstrate your full range of language and may be penalised for content.</li>
              <li>• Always consider your target reader and use a register appropriate to your task.</li>
              <li>• Planning your answer before writing will help you organise your ideas effectively.</li>
              <li>• Allow time to check your work for errors in grammar, spelling and punctuation.</li>
            </ul>
          </div>
        )}

        {/* Exercise List */}
        <ExerciseList
          exercises={exercises}
          currentExercise={currentExercise}
          onSelectExercise={handleSelectExercise}
          completedIds={completedIds}
        />

        {!currentExercise ? (
          <div className="text-center py-12">
            <PenLine className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No hay ejercicios disponibles
            </h2>
            <p className="text-slate-600 mb-4">
              {user?.level 
                ? `No encontramos ejercicios de escritura para el nivel ${user.level}`
                : 'Selecciona un nivel para ver los ejercicios'}
            </p>
            <Button onClick={() => navigate('/level-select')} className="rounded-full">
              Seleccionar Nivel
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Writing Area */}
            <div className="animate-fade-in-up">
              <div className="bg-white border border-slate-200 p-6">
                {/* Exercise Info */}
                <div className="mb-6">
                  <span className={`level-badge level-${currentExercise.level.toLowerCase()} mb-3`}>
                    {currentExercise.level}
                  </span>
                  <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-1">
                    {currentExercise.title}
                  </h1>
                  <p className="text-sm text-orange-600">{currentExercise.title_es}</p>
                </div>

                {/* Task Instructions */}
                <div className="mb-6 space-y-4">
                  {/* Task Type Badge */}
                  {currentExercise.content?.task_type && (
                    <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full capitalize">
                      {currentExercise.content.task_type}
                    </span>
                  )}

                  {/* Instructions */}
                  {currentExercise.content?.instructions && (
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-400 text-sm">
                      <pre className="text-blue-800 whitespace-pre-wrap font-sans text-sm">{currentExercise.content.instructions}</pre>
                      {currentExercise.content.instructions_es && (
                        <pre className="text-blue-600 mt-1 italic whitespace-pre-wrap font-sans text-xs">{currentExercise.content.instructions_es}</pre>
                      )}
                    </div>
                  )}

                  {/* Context */}
                  {currentExercise.content?.context && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
                      <pre className="text-slate-700 whitespace-pre-wrap font-sans text-sm">{currentExercise.content.context}</pre>
                      {currentExercise.content.context_es && (
                        <pre className="text-sm text-slate-500 mt-2 italic whitespace-pre-wrap font-sans">{currentExercise.content.context_es}</pre>
                      )}
                    </div>
                  )}

                  {/* Context Image (e.g. website screenshot for article tasks) */}
                  {currentExercise.content?.context_image && (
                    <div className={currentExercise.content?.context_image_fullwidth ? "w-full" : "flex justify-center"}>
                      <img src={currentExercise.content.context_image} alt="Task context" className={currentExercise.content?.context_image_fullwidth ? "w-full rounded-lg border border-slate-200" : "max-w-full rounded border border-slate-200"} />
                    </div>
                  )}

                  {/* Question/Prompt Box */}
                  <div className="p-4 bg-[#FDFBF7] border-l-4 border-orange-500">
                    {/* Question Number */}
                    {currentExercise.content?.question_number && (
                      <div className="mb-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-bold text-lg">
                          {currentExercise.content.question_number}
                        </span>
                      </div>
                    )}
                    
                    {/* Announcement/Email for Part 2 tasks */}
                    {currentExercise.content?.announcement && (
                      <div className="mb-3 p-3 bg-white border border-slate-200 rounded-sm">
                        <pre className="text-slate-700 whitespace-pre-wrap font-sans text-sm">
                          {currentExercise.content.announcement}
                        </pre>
                        {currentExercise.content.announcement_es && (
                          <pre className="text-slate-500 whitespace-pre-wrap font-sans text-xs mt-2 italic">
                            {currentExercise.content.announcement_es}
                          </pre>
                        )}
                      </div>
                    )}
                    {currentExercise.content?.email_received && (
                      <div className="mb-3 p-3 bg-white border border-slate-200 rounded-sm">
                        <pre className="text-slate-700 whitespace-pre-wrap font-sans text-sm">
                          {currentExercise.content.email_received}
                        </pre>
                        {currentExercise.content.email_received_es && (
                          <pre className="text-slate-500 whitespace-pre-wrap font-sans text-xs mt-2 italic">
                            {currentExercise.content.email_received_es}
                          </pre>
                        )}
                      </div>
                    )}

                    {/* Main Question (for essays) */}
                    {currentExercise.content?.question && (
                      <>
                        <pre className="text-slate-900 font-medium mb-2 whitespace-pre-wrap font-sans text-sm">{currentExercise.content.question}</pre>
                        {currentExercise.content.question_es && (
                          <pre className="text-sm text-slate-500 italic whitespace-pre-wrap font-sans">{currentExercise.content.question_es}</pre>
                        )}
                      </>
                    )}

                    {/* Legacy prompt format */}
                    {currentExercise.content?.prompt && !currentExercise.content?.question && (
                      <>
                        <p className="text-slate-700 mb-2">{currentExercise.content.prompt}</p>
                        {currentExercise.content.prompt_es && (
                          <p className="text-sm text-slate-500 italic">{currentExercise.content.prompt_es}</p>
                        )}
                      </>
                    )}

                    {/* Task instruction */}
                    {currentExercise.content?.task && (
                      <p className="text-slate-700 font-medium mt-3">{currentExercise.content.task}</p>
                    )}
                  </div>

                  {/* Notes for Essay */}
                  {currentExercise.content?.notes && currentExercise.content.notes.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                      <p className="text-sm font-medium text-amber-800 mb-2">Notes / Write about:</p>
                      <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                        {currentExercise.content.notes.map((note, idx) => (
                          <li key={idx}>
                            {note}
                            {currentExercise.content.notes_es && currentExercise.content.notes_es[idx] && (
                              <span className="text-amber-600 italic"> ({currentExercise.content.notes_es[idx]})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Word count requirements */}
                <div className="mb-4 flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    Palabras recomendadas: {currentExercise.content?.word_count?.min || currentExercise.content?.min_words || 100}-{currentExercise.content?.word_count?.max || currentExercise.content?.max_words || 190}
                  </span>
                  <span className={`font-medium ${
                    wordCount >= (currentExercise.content?.word_count?.min || currentExercise.content?.min_words || 100) 
                      ? 'text-green-600' 
                      : 'text-slate-600'
                  }`}>
                    {wordCount} palabras
                  </span>
                </div>

                {/* Text Area */}
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe tu respuesta aquí..."
                  className="min-h-[300px] resize-none font-['Merriweather'] text-slate-700 leading-relaxed"
                  disabled={submitting || feedback}
                  data-testid="writing-textarea"
                />

                {/* Actions */}
                <div className="mt-4 flex gap-3">
                  {!feedback ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || wordCount < 20}
                      className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-full"
                      data-testid="submit-writing-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analizando con IA...
                        </>
                      ) : (
                        'Obtener Feedback'
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleReset}
                      className="flex-1 bg-orange-600 hover:bg-orange-500 rounded-full"
                      data-testid="reset-writing-btn"
                    >
                      Escribir Nuevo Texto
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Feedback Panel - Below the writing area */}
            <div className="animate-fade-in-up animation-delay-200">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Feedback de IA
                </h2>

                {!feedback ? (
                  <div className="text-center py-8">
                    <PenLine className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">
                      Escribe tu texto y haz clic en "Obtener Feedback" para recibir correcciones personalizadas
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Score */}
                    <div className={`p-4 rounded-sm ${
                      feedback.result?.score >= 70 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-orange-50 border border-orange-200'
                    }`}>
                      <p className="text-2xl font-bold">{feedback.result?.score || 0}%</p>
                      <p className="text-sm text-slate-600">Puntuación General</p>
                    </div>

                    {/* Detailed Feedback */}
                    {feedback.detailed_feedback && (
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* English Feedback */}
                        {feedback.detailed_feedback.feedback_en && (
                          <div>
                            <h3 className="font-medium text-slate-900 mb-2">Feedback (English)</h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                              {feedback.detailed_feedback.feedback_en}
                            </p>
                          </div>
                        )}

                        {/* Spanish Feedback */}
                        {feedback.detailed_feedback.feedback_es && (
                          <div>
                            <h3 className="font-medium text-slate-900 mb-2">Feedback (Español)</h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                              {feedback.detailed_feedback.feedback_es}
                            </p>
                          </div>
                        )}

                        {/* Grammar Errors */}
                        {feedback.detailed_feedback.grammar_errors?.length > 0 && (
                          <div>
                            <h3 className="font-medium text-slate-900 mb-2">Errores Gramaticales</h3>
                            <ul className="space-y-1">
                              {feedback.detailed_feedback.grammar_errors.map((error, i) => (
                                <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                                  <span className="text-red-400">•</span>
                                  {typeof error === 'object' 
                                    ? (error.error || error.message || error.original || JSON.stringify(error))
                                    : error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Vocabulary Suggestions */}
                        {feedback.detailed_feedback.vocabulary_suggestions?.length > 0 && (
                          <div>
                            <h3 className="font-medium text-slate-900 mb-2">Sugerencias de Vocabulario</h3>
                            <ul className="space-y-1">
                              {feedback.detailed_feedback.vocabulary_suggestions.map((suggestion, i) => (
                                <li key={i} className="text-sm text-blue-600 flex items-start gap-2">
                                  <span className="text-blue-400">•</span>
                                  {typeof suggestion === 'object' 
                                    ? (suggestion.suggested 
                                        ? `"${suggestion.original}" → "${suggestion.suggested}"${suggestion.comment ? ` (${suggestion.comment})` : ''}`
                                        : JSON.stringify(suggestion))
                                    : suggestion}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Strengths */}
                        {feedback.detailed_feedback.strengths?.length > 0 && (
                          <div>
                            <h3 className="font-medium text-slate-900 mb-2">Puntos Fuertes</h3>
                            <ul className="space-y-1">
                              {feedback.detailed_feedback.strengths.map((strength, i) => (
                                <li key={i} className="text-sm text-green-600 flex items-start gap-2">
                                  <span className="text-green-400">✓</span>
                                  {typeof strength === 'object' 
                                    ? (strength.point || strength.strength || JSON.stringify(strength))
                                    : strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
